import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useFlowAccountTimeline } from './hooks';
import { entryTitle, entryMetaText, EntryDetail, KIND_CONFIG, bucketLabel, bucketOrder, fmtDay } from './AccountTimeline';
import { SfdcCloudIcon } from '../links';

/**
 * Standalone "Signals" tab (Bucket B follow-up, GTMOPS-2162): shows ONLY
 * the `kind: 'signal'` entries from the same account-timeline endpoint
 * AccountTimeline already renders as part of its server-merged stream.
 *
 * This is a deliberate, explicitly-authorized duplicate of AccountTimeline's
 * row JSX shell — the repo owner reviewed the Bucket B interleaved-timeline
 * build live and asked for signals to ALSO get their own separate tab, in
 * addition to (not instead of) the interleaved Timeline + Signals filter
 * pill. The row-rendering LOGIC (title, meta text, expand-to-detail body,
 * day formatting, bucket grouping/ordering) is reused verbatim from
 * AccountTimeline's exported helpers — only the markup shell is
 * duplicated, and only because this panel's rows don't need the filter-pill
 * strip AccountTimeline shows (this panel is signals-only already).
 */
export function AccountSignalsPanel({ accountId }: { accountId: string }) {
  const { data, isLoading, error } = useFlowAccountTimeline(accountId);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const muted = 'text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center';

  if (isLoading) return <div className={muted}>Loading timeline…</div>;
  if (error) return <div className="text-[12px] text-content-danger dark:text-red-400 py-4 text-center">Timeline failed to load.</div>;
  if (!data) return null;
  if (!data.hasAccess) return <div className={muted}>No timeline access for this account.</div>;

  const signals = data.entries.filter(e => e.kind === 'signal');
  if (signals.length === 0) return <div className={muted}>No signals yet.</div>;

  // Group by server bucket, ordered the same way AccountTimeline does:
  // upcoming → months desc → undated.
  const buckets = new Map<string, typeof signals>();
  for (const e of signals) {
    const list = buckets.get(e.bucket) ?? [];
    list.push(e);
    buckets.set(e.bucket, list);
  }
  const ordered = [...buckets.entries()].sort((a, b) => bucketOrder(b[0]).localeCompare(bucketOrder(a[0])));

  return (
    <div>
      {ordered.map(([bucket, entries]) => (
        <div key={bucket} className="mb-2">
          <div className="sticky top-0 z-[1] bg-white dark:bg-nightshade-900 text-[10.5px] uppercase tracking-wide font-semibold text-content-tertiary dark:text-nightshade-500 py-1">
            {bucketLabel(bucket)}
          </div>
          {entries.map(e => {
            const key = `${e.kind}-${e.id}`;
            const isExpanded = expandedKey === key;
            const { Icon, tileClass } = KIND_CONFIG[e.kind];
            return (
              <div key={key} className="border-b border-primary/40 dark:border-nightshade-800 last:border-b-0">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedKey(isExpanded ? null : key)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpandedKey(isExpanded ? null : key); }
                  }}
                  className="w-full flex gap-2 py-1.5 text-[12px] text-left items-start hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded cursor-pointer"
                >
                  <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-1">{fmtDay(e.effectiveAt)}</span>
                  <span className={`w-[22px] h-[22px] shrink-0 rounded-md flex items-center justify-center ${tileClass}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 pt-px">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-medium text-content-primary dark:text-nightshade-100">{entryTitle(e)}</span>
                      <SfdcCloudIcon kind="Events_and_Signals__c" id={e.id} />
                    </span>
                    {entryMetaText(e) && (
                      <span className="text-content-tertiary dark:text-nightshade-500">
                        {' · '}{entryMetaText(e)}
                      </span>
                    )}
                  </span>
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
                </div>
                {isExpanded && <EntryDetail entry={e} />}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
